import React, { useState, useEffect, useMemo } from 'react';
import './ExperimentSimulator.css';

const ExperimentSimulator = ({ onClose }) => {
  // Core experimental parameters based on EXPERIMENT.md
  const [params, setParams] = useState({
    // Participants
    nExperts: 50,
    nLaypeople: 200,
    
    // Experimental design
    nArtists: 12,
    nAIModels: 5,
    nImagesPerCondition: 6,
    nRealPaintings: 18,
    
    // Trial structure - Fixed based on EXPERIMENT.md
    nTrialsPerArtist: 12,
    
    // Post-task
    includePostTaskChecks: true,
    nPostTaskImages: 30,
    
    // Timing parameters (in seconds)
    familiarizationDuration: 40,
    avgTrialDuration: 8,
    itiDuration: 0.6,  // 400-800ms average
    
    // Statistical parameters
    withinPersonCorr: 0.15, // Within-person correlation (0.10-0.20 range from EXPERIMENT.md)
    targetPower: 0.8,
    alpha: 0.05,
    
    // Expected effects (percentage points)
    expectedAIvsRealDiff: 10, // Expected difference in Style Fidelity Index
    expectedContentStyleShift: 14, // Expected content-style disentanglement effect
    
    // Data quality
    participantExclusionRate: 0.05, // 5% participant exclusion
    trialExclusionRate: 0.10, // 10% trial exclusion
    
    // Cost parameters
    costPerParticipant: 20,
  });

  // Trial family distribution based on EXPERIMENT.md Section 8
  const TRIAL_FAMILIES = {
    T1: { name: 'AI vs Real (content-matched)', trials: 6, percent: 50, description: 'Primary fidelity measure' },
    T2: { name: 'AI vs AI (cross-model)', trials: 3, percent: 25, description: 'Model consistency assessment' },
    T3: { name: 'Real vs Real (ceiling)', trials: 1, percent: 8, description: 'Human ceiling performance' },
    T4: { name: 'Content disentanglement', trials: 2, percent: 17, description: 'Style vs content reliance' }
  };

  // Calculate confidence intervals based on EXPERIMENT.md Section 14
  const calculateConfidenceIntervals = (nParticipants, withinCorr, nArtists = 12) => {
    // For single model-artist pair
    const nTrialsT1PerArtist = TRIAL_FAMILIES.T1.trials;
    const effectiveN = nParticipants * nTrialsT1PerArtist;
    
    // Standard error for binomial proportion near 0.5
    const seSingle = Math.sqrt(0.25 / effectiveN);
    const ciSingle = 1.96 * seSingle * 100; // Convert to percentage points
    
    // For averages across artists
    // Account for within-person correlation
    const varReduction = 1 / nArtists;
    const corrPenalty = 1 + (nArtists - 1) * withinCorr;
    const seAverage = Math.sqrt((0.25 * corrPenalty) / (effectiveN * nArtists));
    const ciAverage = 1.96 * seAverage * 100;
    
    return {
      single: ciSingle,
      average: ciAverage
    };
  };

  // Calculate power for detecting differences
  const calculatePower = (nParticipants, effectSizePP, nTrials, alpha = 0.05) => {
    // Convert percentage points to proportion
    const effectSize = effectSizePP / 100;
    
    // Standard error under null hypothesis
    const se = Math.sqrt(0.25 / (nParticipants * nTrials));
    
    // Z-scores
    const zAlpha = 1.96; // Two-tailed alpha = 0.05
    const criticalValue = zAlpha * se;
    
    // Power calculation
    const nonCentrality = effectSize / se;
    const zPower = nonCentrality - zAlpha;
    const power = normalCDF(zPower);
    
    return power;
  };

  // Calculate sample size needed for target power
  const calculateSampleSize = (effectSizePP, targetPower, nTrials, alpha = 0.05) => {
    const effectSize = effectSizePP / 100;
    const zAlpha = 1.96;
    const zPower = inverseCDF(targetPower);
    
    // Solve for n
    const variance = 0.25; // Maximum variance at p=0.5
    const n = variance * Math.pow(zAlpha + zPower, 2) / (Math.pow(effectSize, 2) * nTrials);
    
    return Math.ceil(n);
  };

  // Calculate mixed-effects model components
  const calculateMixedEffectsComponents = (params) => {
    const totalTrials = params.nArtists * params.nTrialsPerArtist;
    const nParticipants = params.nExperts + params.nLaypeople;
    
    // Variance components (simplified estimates)
    const varParticipant = 0.05; // Random intercept for participant
    const varImage = 0.02; // Random intercept for image  
    const varSlope = 0.03; // Random slope for source by participant
    const varResidual = 1.0; // Residual variance
    
    // Intraclass correlations
    const iccParticipant = varParticipant / (varParticipant + varImage + varSlope + varResidual);
    const iccImage = varImage / (varParticipant + varImage + varSlope + varResidual);
    
    // Design effect
    const avgClusterSize = totalTrials;
    const designEffect = 1 + (avgClusterSize - 1) * iccParticipant;
    
    // Effective sample size
    const effectiveSampleSize = (nParticipants * totalTrials) / designEffect;
    
    return {
      varParticipant,
      varImage,
      varSlope,
      varResidual,
      iccParticipant,
      iccImage,
      designEffect,
      effectiveSampleSize,
      avgClusterSize
    };
  };

  // Calculate Style Fidelity Index metrics
  const calculateStyleFidelityMetrics = (params) => {
    const nLay = params.nLaypeople;
    const nExp = params.nExperts;
    const nCombined = nLay + nExp;
    
    // Get confidence intervals for different groups (from EXPERIMENT.md)
    const ciLay = calculateConfidenceIntervals(nLay, params.withinPersonCorr);
    const ciExp = calculateConfidenceIntervals(nExp, params.withinPersonCorr);
    const ciCombined = calculateConfidenceIntervals(nCombined, params.withinPersonCorr);
    
    // Calculate power for detecting 10pp difference (60% vs 50%)
    const nT1Trials = TRIAL_FAMILIES.T1.trials;
    const powerLay = calculatePower(nLay, 10, nT1Trials);
    const powerExp = calculatePower(nExp, 10, nT1Trials);
    const powerCombined = calculatePower(nCombined, 10, nT1Trials);
    
    // Sample sizes needed for 80% power
    const neededLay = calculateSampleSize(10, 0.8, nT1Trials);
    const neededExp = calculateSampleSize(10, 0.8, nT1Trials);
    
    return {
      lay: {
        ciSingle: ciLay.single,
        ciAverage: ciLay.average,
        power: powerLay,
        needed: neededLay
      },
      expert: {
        ciSingle: ciExp.single,
        ciAverage: ciExp.average,
        power: powerExp,
        needed: neededExp
      },
      combined: {
        ciSingle: ciCombined.single,
        ciAverage: ciCombined.average,
        power: powerCombined,
        needed: Math.max(neededLay, neededExp)
      }
    };
  };

  // Calculate cross-model comparison metrics (T2 trials)
  const calculateCrossModelMetrics = (params) => {
    const nLay = params.nLaypeople;
    const nT2Trials = TRIAL_FAMILIES.T2.trials;
    
    // For single artist
    const nChoicesSingleArtist = nLay * nT2Trials / 2; // Divided by 2 for pairwise
    const ciSingleArtist = 1.96 * Math.sqrt(0.25 / nChoicesSingleArtist) * 100;
    
    // Pooled across all artists
    const nChoicesPooled = nChoicesSingleArtist * params.nArtists;
    const ciPooled = 1.96 * Math.sqrt(0.25 / nChoicesPooled) * 100;
    
    return {
      singleArtist: {
        nChoices: Math.round(nChoicesSingleArtist),
        ci: ciSingleArtist
      },
      pooled: {
        nChoices: Math.round(nChoicesPooled),
        ci: ciPooled
      }
    };
  };

  // Calculate content-style disentanglement metrics (T4 trials)
  const calculateContentStyleMetrics = (params) => {
    const nLay = params.nLaypeople;
    const nExp = params.nExperts;
    const nT4Trials = TRIAL_FAMILIES.T4.trials;
    
    // Detectable shifts with 80% power (from EXPERIMENT.md)
    const detectableLay = 14; // percentage points
    const detectableExp = 28; // percentage points
    const detectableCombined = 12.5; // percentage points
    
    // Calculate actual power for expected shift
    const powerLay = calculatePower(nLay, params.expectedContentStyleShift, nT4Trials);
    const powerExp = calculatePower(nExp, params.expectedContentStyleShift, nT4Trials);
    const powerCombined = calculatePower(nLay + nExp, params.expectedContentStyleShift, nT4Trials);
    
    return {
      lay: {
        detectableShift: detectableLay,
        power: powerLay
      },
      expert: {
        detectableShift: detectableExp,
        power: powerExp
      },
      combined: {
        detectableShift: detectableCombined,
        power: powerCombined
      }
    };
  };

  // Calculate data loss impacts
  const calculateDataLossImpact = (baseCI, participantLoss, trialLoss) => {
    // Effective sample size reduction
    const effectiveReduction = (1 - participantLoss) * (1 - trialLoss);
    
    // CI increases with square root of sample size reduction
    const adjustedCI = baseCI / Math.sqrt(effectiveReduction);
    
    return adjustedCI;
  };

  // Calculate all metrics
  const metrics = useMemo(() => {
    const totalParticipants = params.nExperts + params.nLaypeople;
    const totalTrials = params.nArtists * params.nTrialsPerArtist;
    const totalDataPoints = totalParticipants * totalTrials;
    
    // Timing calculations
    const setupTime = 180; // 3 minutes for consent, instructions, demographics
    const familiarizationTimeTotal = params.nArtists * (params.familiarizationDuration + 3);
    const trialsTimeTotal = totalTrials * params.avgTrialDuration;
    const postTaskTime = params.includePostTaskChecks ? params.nPostTaskImages * 5 : 0;
    const totalTimeSeconds = setupTime + familiarizationTimeTotal + trialsTimeTotal + postTaskTime;
    const totalTimeMinutes = totalTimeSeconds / 60;
    
    // Style Fidelity Index metrics
    const styleFidelity = calculateStyleFidelityMetrics(params);
    
    // Mixed-effects model components
    const mixedEffects = calculateMixedEffectsComponents(params);
    
    // Cross-model metrics
    const crossModel = calculateCrossModelMetrics(params);
    
    // Content-style disentanglement
    const contentStyle = calculateContentStyleMetrics(params);
    
    // Data loss impacts
    const dataLossImpact = {
      lay: calculateDataLossImpact(
        styleFidelity.lay.ciSingle,
        params.participantExclusionRate,
        params.trialExclusionRate
      ),
      expert: calculateDataLossImpact(
        styleFidelity.expert.ciSingle,
        params.participantExclusionRate,
        params.trialExclusionRate
      )
    };
    
    // Image requirements
    const subjectTypes = 3; // typical, plausible, anachronistic
    const totalAIImages = params.nArtists * params.nAIModels * subjectTypes * params.nImagesPerCondition;
    const totalRealImages = params.nArtists * params.nRealPaintings;
    const totalImages = totalAIImages + totalRealImages;
    
    // Cost estimates
    const totalCost = totalParticipants * params.costPerParticipant;
    
    // Data storage estimate
    const bytesPerTrial = 500;
    const totalDataSizeMB = (totalDataPoints * bytesPerTrial) / (1024 * 1024);
    
    // Calculate trials per model in T1
    const trialsPerModelT1 = Math.floor(TRIAL_FAMILIES.T1.trials / params.nAIModels);
    
    return {
      totalParticipants,
      totalTrials,
      totalDataPoints,
      totalTimeMinutes,
      completionTimeFormatted: formatTime(totalTimeMinutes),
      styleFidelity,
      mixedEffects,
      crossModel,
      contentStyle,
      dataLossImpact,
      totalAIImages,
      totalRealImages,
      totalImages,
      estimatedCost: totalCost,
      dataSizeMB: totalDataSizeMB,
      trialsPerModelT1
    };
  }, [params]);

  // Helper functions
  function normalCDF(z) {
    const t = 1 / (1 + 0.2316419 * Math.abs(z));
    const d = 0.3989423 * Math.exp(-z * z / 2);
    const p = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
    return z > 0 ? 1 - p : p;
  }

  function inverseCDF(p) {
    const a = [2.515517, 0.802853, 0.010328];
    const b = [1.432788, 0.189269, 0.001308];
    const t = Math.sqrt(-2 * Math.log(1 - p));
    return t - (a[0] + a[1] * t + a[2] * t * t) / (1 + b[0] * t + b[1] * t * t + b[2] * t * t * t);
  }

  function formatTime(minutes) {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  }

  const handleParamChange = (key, value) => {
    let parsedValue = parseFloat(value);
    
    const constraints = {
      nExperts: { min: 10, max: 500 },
      nLaypeople: { min: 10, max: 500 },
      nArtists: { min: 3, max: 12 },
      nAIModels: { min: 2, max: 10 },
      nImagesPerCondition: { min: 3, max: 10 },
      nRealPaintings: { min: 10, max: 40 },
      nPostTaskImages: { min: 10, max: 50 },
      familiarizationDuration: { min: 20, max: 60 },
      avgTrialDuration: { min: 5, max: 15 },
      withinPersonCorr: { min: 0.10, max: 0.20 },
      targetPower: { min: 0.7, max: 0.95 },
      expectedAIvsRealDiff: { min: 5, max: 20 },
      expectedContentStyleShift: { min: 10, max: 30 },
      participantExclusionRate: { min: 0, max: 0.15 },
      trialExclusionRate: { min: 0, max: 0.20 },
      costPerParticipant: { min: 1, max: 100 },
    };
    
    if (constraints[key]) {
      const { min, max } = constraints[key];
      parsedValue = Math.max(min, Math.min(max, parsedValue || min));
    }
    
    setParams(prev => ({
      ...prev,
      [key]: parsedValue
    }));
  };

  // Generate recommendations
  const recommendations = useMemo(() => {
    const recs = [];
    
    // Check completion time
    if (metrics.totalTimeMinutes > 45) {
      recs.push({
        type: 'warning',
        text: `Experiment duration (${metrics.completionTimeFormatted}) exceeds recommended 45 minutes. Consider reducing artists or trials.`
      });
    }
    
    // Check power for Style Fidelity Index
    if (metrics.styleFidelity.lay.power < params.targetPower) {
      const deficit = metrics.styleFidelity.lay.needed - params.nLaypeople;
      recs.push({
        type: 'warning',
        text: `Lay participant power (${(metrics.styleFidelity.lay.power * 100).toFixed(1)}%) below target. Need ${deficit} more participants.`
      });
    }
    
    if (metrics.styleFidelity.expert.power < params.targetPower) {
      const deficit = metrics.styleFidelity.expert.needed - params.nExperts;
      recs.push({
        type: 'warning',
        text: `Expert power (${(metrics.styleFidelity.expert.power * 100).toFixed(1)}%) below target. Need ${deficit} more experts.`
      });
    }
    
    // Check confidence intervals
    if (metrics.styleFidelity.lay.ciSingle > 10) {
      recs.push({
        type: 'info',
        text: `Single artist CI for lay participants (±${metrics.styleFidelity.lay.ciSingle.toFixed(1)}pp) exceeds ±10pp. Consider increasing sample size.`
      });
    }
    
    // Check cross-model comparisons
    if (metrics.crossModel.singleArtist.ci > 15) {
      recs.push({
        type: 'info',
        text: `Cross-model comparison CI (±${metrics.crossModel.singleArtist.ci.toFixed(1)}pp) is wide. Consider more T2 trials.`
      });
    }
    
    // Check content-style disentanglement power
    if (metrics.contentStyle.combined.power < 0.8) {
      recs.push({
        type: 'info',
        text: `Content-style disentanglement power (${(metrics.contentStyle.combined.power * 100).toFixed(1)}%) is below 80%. Expected shift may be too small to detect reliably.`
      });
    }
    
    // Check data loss impact
    if (metrics.dataLossImpact.lay > metrics.styleFidelity.lay.ciSingle * 1.2) {
      recs.push({
        type: 'info',
        text: `Data loss will increase CIs by >20%. Consider oversampling to compensate.`
      });
    }
    
    if (recs.length === 0) {
      recs.push({
        type: 'success',
        text: 'Parameters meet all statistical requirements for the experiment!'
      });
    }
    
    return recs;
  }, [metrics, params]);

  // Generate markdown export
  const generateMarkdownExport = () => {
    const date = new Date().toISOString().split('T')[0];
    const time = new Date().toLocaleTimeString();
    
    let markdown = `# Experiment Parameter Configuration Report
Generated: ${date} at ${time}

## Executive Summary

This report analyzes the statistical power and precision for the **AI Style Perception Experiment** based on the design specified in EXPERIMENT.md. The experiment uses a two-alternative forced-choice (2AFC) paradigm with mixed-effects modeling to estimate the Style Fidelity Index.

### Key Metrics
- **Total Participants**: ${metrics.totalParticipants} (${params.nExperts} experts, ${params.nLaypeople} laypeople)
- **Artists**: ${params.nArtists} artists × ${params.nTrialsPerArtist} trials each
- **AI Models**: ${params.nAIModels} models compared
- **Duration**: ${metrics.completionTimeFormatted} per participant
- **Total Cost**: $${metrics.estimatedCost.toLocaleString()}

---

## 1. Trial Family Distribution (Section 8 of EXPERIMENT.md)

Each artist block contains exactly ${params.nTrialsPerArtist} trials:

| Trial Family | Trials | Percentage | Purpose |
|-------------|--------|------------|---------|
| **T1: AI vs Real** | ${TRIAL_FAMILIES.T1.trials} | ${TRIAL_FAMILIES.T1.percent}% | ${TRIAL_FAMILIES.T1.description} |
| **T2: AI vs AI** | ${TRIAL_FAMILIES.T2.trials} | ${TRIAL_FAMILIES.T2.percent}% | ${TRIAL_FAMILIES.T2.description} |
| **T3: Real vs Real** | ${TRIAL_FAMILIES.T3.trials} | ${TRIAL_FAMILIES.T3.percent}% | ${TRIAL_FAMILIES.T3.description} |
| **T4: Content Disentanglement** | ${TRIAL_FAMILIES.T4.trials} | ${TRIAL_FAMILIES.T4.percent}% | ${TRIAL_FAMILIES.T4.description} |

---

## 2. Style Fidelity Index (Primary Outcome) - Section 14

The Style Fidelity Index measures the probability that an AI-generated image is judged more characteristic than a content-matched real artwork in T1 trials.

### 2.1 Confidence Intervals

| Measure | Lay Participants | Expert Participants | Combined Sample |
|---------|-----------------|---------------------|-----------------|
| **Single model-artist pair** | ±${metrics.styleFidelity.lay.ciSingle.toFixed(1)}pp | ±${metrics.styleFidelity.expert.ciSingle.toFixed(1)}pp | ±${metrics.styleFidelity.combined.ciSingle.toFixed(1)}pp |
| **Average across ${params.nArtists} artists** | ±${metrics.styleFidelity.lay.ciAverage.toFixed(1)}pp | ±${metrics.styleFidelity.expert.ciAverage.toFixed(1)}pp | ±${metrics.styleFidelity.combined.ciAverage.toFixed(1)}pp |

*pp = percentage points. Within-person correlation = ${params.withinPersonCorr.toFixed(2)} (range: 0.10-0.20)*

### 2.2 Statistical Power

Power to detect a 10 percentage point difference (60% vs 50% win rate):

| Group | Current Power | Sample Size Needed (80% power) | Status |
|-------|--------------|--------------------------------|--------|
| **Lay Participants** | ${(metrics.styleFidelity.lay.power * 100).toFixed(1)}% | ${metrics.styleFidelity.lay.needed} | ${params.nLaypeople >= metrics.styleFidelity.lay.needed ? '✓ Sufficient' : `Need ${metrics.styleFidelity.lay.needed - params.nLaypeople} more`} |
| **Expert Participants** | ${(metrics.styleFidelity.expert.power * 100).toFixed(1)}% | ${metrics.styleFidelity.expert.needed} | ${params.nExperts >= metrics.styleFidelity.expert.needed ? '✓ Sufficient' : `Need ${metrics.styleFidelity.expert.needed - params.nExperts} more`} |
| **Combined Sample** | ${(metrics.styleFidelity.combined.power * 100).toFixed(1)}% | - | ${metrics.styleFidelity.combined.power >= 0.8 ? '✓ Adequate' : '⚠️ Below target'} |

---

## 3. Cross-Model Comparisons (T2 Trials) - Section 14

T2 trials directly compare AI models against each other, analyzed using Bradley-Terry models.

| Comparison Level | N Choices | 95% CI | Interpretation |
|-----------------|-----------|--------|----------------|
| **Single artist, one model pair** | ~${metrics.crossModel.singleArtist.nChoices} | ±${metrics.crossModel.singleArtist.ci.toFixed(1)}pp | Can detect ~${(metrics.crossModel.singleArtist.ci * 2).toFixed(0)}pp differences |
| **Pooled across all artists** | ~${metrics.crossModel.pooled.nChoices} | ±${metrics.crossModel.pooled.ci.toFixed(1)}pp | Can detect ~${(metrics.crossModel.pooled.ci * 2).toFixed(0)}pp differences |

---

## 4. Content-Style Disentanglement (T4 Trials) - Section 14

T4 trials test whether AI models rely on typical subject matter vs. abstract stylistic features.

| Group | Detectable Shift (80% power) | Current Power for ${params.expectedContentStyleShift}pp shift |
|-------|------------------------------|--------------------------------------------------|
| **Lay Participants** | ${metrics.contentStyle.lay.detectableShift}pp | ${(metrics.contentStyle.lay.power * 100).toFixed(1)}% |
| **Expert Participants** | ${metrics.contentStyle.expert.detectableShift}pp | ${(metrics.contentStyle.expert.power * 100).toFixed(1)}% |
| **Combined Sample** | ${metrics.contentStyle.combined.detectableShift}pp | ${(metrics.contentStyle.combined.power * 100).toFixed(1)}% |

---

## 5. Mixed-Effects Model Components - Section 13

The analysis uses mixed-effects logistic regression with the following variance components:

| Component | Value | Description |
|-----------|-------|-------------|
| **Random intercept (participant)** | ${metrics.mixedEffects.varParticipant.toFixed(3)} | Between-participant variance |
| **Random intercept (image)** | ${metrics.mixedEffects.varImage.toFixed(3)} | Between-image variance |
| **Random slope (source × participant)** | ${metrics.mixedEffects.varSlope.toFixed(3)} | Individual AI preference variance |
| **Residual variance** | ${metrics.mixedEffects.varResidual.toFixed(3)} | Unexplained variance |
| **Design effect** | ${metrics.mixedEffects.designEffect.toFixed(2)} | Clustering penalty |
| **Effective sample size** | ${Math.round(metrics.mixedEffects.effectiveSampleSize)} | Equivalent independent observations |

---

## 6. Data Loss Impact - Section 14

Expected data loss: ${(params.participantExclusionRate * 100).toFixed(0)}% participants, ${(params.trialExclusionRate * 100).toFixed(0)}% trials

| Measure | Original CI | After Data Loss | Change |
|---------|------------|-----------------|--------|
| **Lay (single artist)** | ±${metrics.styleFidelity.lay.ciSingle.toFixed(1)}pp | ±${metrics.dataLossImpact.lay.toFixed(1)}pp | +${(metrics.dataLossImpact.lay - metrics.styleFidelity.lay.ciSingle).toFixed(1)}pp |
| **Expert (single artist)** | ±${metrics.styleFidelity.expert.ciSingle.toFixed(1)}pp | ±${metrics.dataLossImpact.expert.toFixed(1)}pp | +${(metrics.dataLossImpact.expert - metrics.styleFidelity.expert.ciSingle).toFixed(1)}pp |

---

## 7. Human Ceiling (T3 Trials)

T3 trials (${TRIAL_FAMILIES.T3.trials} per artist) establish baseline performance by comparing two real artworks. These provide:
- **Separability**: How much choices deviate from 50-50 split
- **Reliability**: Consistency of judgments across participants
- **Ceiling effect**: Upper bound for AI performance claims

*Note: T3 trials are not used to compute the Style Fidelity Index but provide crucial calibration.*

---

## 8. Resource Requirements

### 8.1 Images
- **AI Images**: ${metrics.totalAIImages} (${params.nAIModels} models × ${params.nArtists} artists × 3 subject types × ${params.nImagesPerCondition} variations)
- **Real Artworks**: ${metrics.totalRealImages} (${params.nRealPaintings} per artist)
- **Total Database**: ${metrics.totalImages} images

### 8.2 Data Collection
- **Total Trials**: ${metrics.totalDataPoints.toLocaleString()} across all participants
- **Diagnostic Responses**: ${metrics.totalDataPoints.toLocaleString()} style dimension choices
- **Post-Task Checks**: ${params.includePostTaskChecks ? (metrics.totalParticipants * params.nPostTaskImages * 2).toLocaleString() : '0'} (recognition + attribution)
- **Data Size**: ~${metrics.dataSizeMB.toFixed(1)} MB

### 8.3 Financial
- **Participant Costs**: $${metrics.estimatedCost.toLocaleString()}
- **Platform Fees (20%)**: $${(metrics.estimatedCost * 0.2).toFixed(0)}
- **Total Budget**: $${(metrics.estimatedCost * 1.2).toFixed(0)}

---

## 9. Recommendations

${recommendations.map(rec => {
  if (rec.type === 'warning') {
    return `⚠️ **WARNING**: ${rec.text}`;
  } else if (rec.type === 'info') {
    return `ℹ️ **INFO**: ${rec.text}`;
  } else if (rec.type === 'success') {
    return `✓ **SUCCESS**: ${rec.text}`;
  }
}).join('\n')}

---

## 10. Key Design Decisions

### Sample Size Justification
- Current design provides ${metrics.styleFidelity.combined.power >= 0.8 ? 'adequate' : 'insufficient'} power for primary contrasts
- Single artist precision: ±${metrics.styleFidelity.combined.ciSingle.toFixed(1)}pp allows meaningful comparisons
- Cross-artist averages: ±${metrics.styleFidelity.combined.ciAverage.toFixed(1)}pp enables model ranking

### Statistical Approach
- **Mixed-effects logistic regression** accounts for clustering
- **Random effects** for participants and images prevent underestimated SEs
- **Bradley-Terry models** for T2 provide model rankings
- **FDR control** for multiple comparisons maintains credibility

### Trial Allocation
- 50% to T1 (primary outcome) maximizes precision for Style Fidelity Index
- 25% to T2 enables robust cross-model comparisons
- 17% to T4 provides adequate power for content-style disentanglement
- 8% to T3 establishes human ceiling with minimal trials

---

*This analysis follows the statistical framework specified in EXPERIMENT.md Sections 13-14.*
`;

    return markdown;
  };

  // Function to handle markdown export
  const handleExportMarkdown = () => {
    const markdown = generateMarkdownExport();
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    a.download = `experiment-parameters-${timestamp}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Generate power curve for visualization
  function generatePowerCurve(effectSizePP) {
    const points = [];
    const nT1Trials = TRIAL_FAMILIES.T1.trials;
    
    for (let n = 10; n <= 500; n += 10) {
      const power = calculatePower(n, effectSizePP, nT1Trials);
      const x = 40 + (n / 500) * 340;
      const y = 180 - power * 140;
      points.push(`${x},${y}`);
    }
    return `M ${points.join(' L ')}`;
  }

  // Apply preset configurations
  function applyPreset(preset) {
    const presets = {
      minimal: {
        nExperts: 20,
        nLaypeople: 50,
        nArtists: 6,
        nAIModels: 3,
        nImagesPerCondition: 6,
        nRealPaintings: 18,
        nPostTaskImages: 15,
        familiarizationDuration: 30,
      },
      standard: {
        nExperts: 50,
        nLaypeople: 200,
        nArtists: 12,
        nAIModels: 5,
        nImagesPerCondition: 6,
        nRealPaintings: 18,
        nPostTaskImages: 30,
        familiarizationDuration: 40,
      },
      comprehensive: {
        nExperts: 75,
        nLaypeople: 300,
        nArtists: 12,
        nAIModels: 6,
        nImagesPerCondition: 8,
        nRealPaintings: 24,
        nPostTaskImages: 40,
        familiarizationDuration: 45,
      },
      'power-optimized': {
        nExperts: 60,
        nLaypeople: 240,
        nArtists: 12,
        nAIModels: 5,
        nImagesPerCondition: 6,
        nRealPaintings: 18,
        nPostTaskImages: 30,
        familiarizationDuration: 40,
        targetPower: 0.9,
      },
    };
    
    if (presets[preset]) {
      setParams(prev => ({
        ...prev,
        ...presets[preset]
      }));
    }
  }

  return (
    <div className="simulator-container">
      <div className="simulator-header">
        <h2>Experiment Parameter Configuration</h2>
        <div className="header-buttons">
          <button className="export-button" onClick={handleExportMarkdown} title="Export as Markdown">
            📄 Export Report
          </button>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
      </div>
      
      <div className="simulator-content">
        <div className="params-section">
          <h3>Parameters</h3>
          
          <div className="param-group">
            <h4>Participants</h4>
            <div className="param-row">
              <label>
                Expert Participants:
                <input 
                  type="number" 
                  value={params.nExperts}
                  onChange={(e) => handleParamChange('nExperts', e.target.value)}
                  min="10" max="500"
                />
                <input 
                  type="range" 
                  value={params.nExperts}
                  onChange={(e) => handleParamChange('nExperts', e.target.value)}
                  min="10" max="500"
                />
              </label>
            </div>
            <div className="param-row">
              <label>
                Lay Participants:
                <input 
                  type="number" 
                  value={params.nLaypeople}
                  onChange={(e) => handleParamChange('nLaypeople', e.target.value)}
                  min="10" max="500"
                />
                <input 
                  type="range" 
                  value={params.nLaypeople}
                  onChange={(e) => handleParamChange('nLaypeople', e.target.value)}
                  min="10" max="500"
                />
              </label>
            </div>
          </div>

          <div className="param-group">
            <h4>Experimental Design</h4>
            <div className="param-row">
              <label>
                Number of Artists:
                <input 
                  type="number" 
                  value={params.nArtists}
                  onChange={(e) => handleParamChange('nArtists', e.target.value)}
                  min="3" max="12"
                />
                <input 
                  type="range" 
                  value={params.nArtists}
                  onChange={(e) => handleParamChange('nArtists', e.target.value)}
                  min="3" max="12"
                />
              </label>
            </div>
            <div className="param-row">
              <label>
                AI Models Compared:
                <input 
                  type="number" 
                  value={params.nAIModels}
                  onChange={(e) => handleParamChange('nAIModels', e.target.value)}
                  min="2" max="10"
                />
                <input 
                  type="range" 
                  value={params.nAIModels}
                  onChange={(e) => handleParamChange('nAIModels', e.target.value)}
                  min="2" max="10"
                />
              </label>
            </div>
            <div className="param-row">
              <label>
                Trials per Artist:
                <input 
                  type="number" 
                  value={params.nTrialsPerArtist}
                  disabled
                  title="Fixed at 12 trials per EXPERIMENT.md"
                />
                <span style={{fontSize: '11px', color: '#6c757d', marginLeft: '8px'}}>
                  Fixed (T1:6, T2:3, T3:1, T4:2)
                </span>
              </label>
            </div>
          </div>

          <div className="param-group">
            <h4>Statistical Parameters</h4>
            <div className="param-row">
              <label>
                Within-Person Correlation:
                <input 
                  type="number" 
                  value={params.withinPersonCorr}
                  onChange={(e) => handleParamChange('withinPersonCorr', e.target.value)}
                  min="0.10" max="0.20" step="0.01"
                />
                <input 
                  type="range" 
                  value={params.withinPersonCorr}
                  onChange={(e) => handleParamChange('withinPersonCorr', e.target.value)}
                  min="0.10" max="0.20" step="0.01"
                />
                <span style={{fontSize: '11px', color: '#6c757d', marginLeft: '8px'}}>
                  (0.10-0.20 per EXPERIMENT.md)
                </span>
              </label>
            </div>
            <div className="param-row">
              <label>
                Target Power:
                <input 
                  type="number" 
                  value={params.targetPower}
                  onChange={(e) => handleParamChange('targetPower', e.target.value)}
                  min="0.7" max="0.95" step="0.05"
                />
                <input 
                  type="range" 
                  value={params.targetPower}
                  onChange={(e) => handleParamChange('targetPower', e.target.value)}
                  min="0.7" max="0.95" step="0.05"
                />
              </label>
            </div>
            <div className="param-row">
              <label>
                Expected AI vs Real Difference (pp):
                <input 
                  type="number" 
                  value={params.expectedAIvsRealDiff}
                  onChange={(e) => handleParamChange('expectedAIvsRealDiff', e.target.value)}
                  min="5" max="20"
                />
                <input 
                  type="range" 
                  value={params.expectedAIvsRealDiff}
                  onChange={(e) => handleParamChange('expectedAIvsRealDiff', e.target.value)}
                  min="5" max="20"
                />
                <span style={{fontSize: '11px', color: '#6c757d', marginLeft: '8px'}}>
                  percentage points
                </span>
              </label>
            </div>
          </div>

          <div className="param-group">
            <h4>Data Quality</h4>
            <div className="param-row">
              <label>
                Participant Exclusion Rate:
                <input 
                  type="number" 
                  value={params.participantExclusionRate}
                  onChange={(e) => handleParamChange('participantExclusionRate', e.target.value)}
                  min="0" max="0.15" step="0.01"
                />
                <input 
                  type="range" 
                  value={params.participantExclusionRate}
                  onChange={(e) => handleParamChange('participantExclusionRate', e.target.value)}
                  min="0" max="0.15" step="0.01"
                />
                <span style={{fontSize: '11px', color: '#6c757d', marginLeft: '8px'}}>
                  {(params.participantExclusionRate * 100).toFixed(0)}%
                </span>
              </label>
            </div>
            <div className="param-row">
              <label>
                Trial Exclusion Rate:
                <input 
                  type="number" 
                  value={params.trialExclusionRate}
                  onChange={(e) => handleParamChange('trialExclusionRate', e.target.value)}
                  min="0" max="0.20" step="0.01"
                />
                <input 
                  type="range" 
                  value={params.trialExclusionRate}
                  onChange={(e) => handleParamChange('trialExclusionRate', e.target.value)}
                  min="0" max="0.20" step="0.01"
                />
                <span style={{fontSize: '11px', color: '#6c757d', marginLeft: '8px'}}>
                  {(params.trialExclusionRate * 100).toFixed(0)}%
                </span>
              </label>
            </div>
          </div>

          <div className="param-group">
            <h4>Cost Settings</h4>
            <div className="param-row">
              <label>
                Cost per Participant ($):
                <input 
                  type="number" 
                  value={params.costPerParticipant}
                  onChange={(e) => handleParamChange('costPerParticipant', e.target.value)}
                  min="1" max="100"
                />
                <input 
                  type="range" 
                  value={params.costPerParticipant}
                  onChange={(e) => handleParamChange('costPerParticipant', e.target.value)}
                  min="1" max="100"
                />
              </label>
            </div>
          </div>
        </div>

        <div className="metrics-section">
          <h3>Analysis</h3>
          
          {/* Trial Family Distribution */}
          <div className="trial-distribution" style={{marginBottom: '16px'}}>
            <h4>Trial Family Distribution (T1-T4)</h4>
            <div className="distribution-bars">
              {Object.entries(TRIAL_FAMILIES).map(([key, family]) => (
                <div key={key} className="distribution-bar">
                  <div className="bar-label" style={{flex: '0 0 200px'}}>
                    <strong>{key}:</strong> {family.name}
                  </div>
                  <div className="bar-container">
                    <div 
                      className="bar-fill" 
                      style={{
                        width: `${(family.trials / params.nTrialsPerArtist) * 100}%`,
                        background: key === 'T1' ? '#007bff' : key === 'T2' ? '#28a745' : key === 'T3' ? '#ffc107' : '#17a2b8'
                      }}
                    >
                      {family.trials} ({family.percent}%)
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{marginTop: '8px', fontSize: '11px', color: '#6c757d'}}>
              Total: {params.nTrialsPerArtist} trials per artist × {params.nArtists} artists = {metrics.totalTrials} trials per participant
            </div>
          </div>

          {/* Style Fidelity Index Metrics */}
          <div className="style-fidelity-metrics" style={{
            background: '#ffffff',
            border: '1px solid #dee2e6',
            borderRadius: '3px',
            padding: '16px',
            marginBottom: '16px'
          }}>
            <h4 style={{margin: '0 0 12px 0', color: '#495057', fontSize: '14px', fontWeight: '500'}}>
              Style Fidelity Index (T1 Trials)
            </h4>
            
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '12px'}}>
              <div>
                <h5 style={{margin: '0 0 8px 0', fontSize: '12px', color: '#6c757d'}}>Lay Participants</h5>
                <div style={{fontSize: '11px'}}>
                  <div>Single artist: ±{metrics.styleFidelity.lay.ciSingle.toFixed(1)}pp</div>
                  <div>Across artists: ±{metrics.styleFidelity.lay.ciAverage.toFixed(1)}pp</div>
                  <div>Power: {(metrics.styleFidelity.lay.power * 100).toFixed(1)}%</div>
                  <div>Need: {metrics.styleFidelity.lay.needed} participants</div>
                </div>
              </div>
              <div>
                <h5 style={{margin: '0 0 8px 0', fontSize: '12px', color: '#6c757d'}}>Expert Participants</h5>
                <div style={{fontSize: '11px'}}>
                  <div>Single artist: ±{metrics.styleFidelity.expert.ciSingle.toFixed(1)}pp</div>
                  <div>Across artists: ±{metrics.styleFidelity.expert.ciAverage.toFixed(1)}pp</div>
                  <div>Power: {(metrics.styleFidelity.expert.power * 100).toFixed(1)}%</div>
                  <div>Need: {metrics.styleFidelity.expert.needed} participants</div>
                </div>
              </div>
              <div>
                <h5 style={{margin: '0 0 8px 0', fontSize: '12px', color: '#6c757d'}}>Combined Sample</h5>
                <div style={{fontSize: '11px'}}>
                  <div>Single artist: ±{metrics.styleFidelity.combined.ciSingle.toFixed(1)}pp</div>
                  <div>Across artists: ±{metrics.styleFidelity.combined.ciAverage.toFixed(1)}pp</div>
                  <div>Power: {(metrics.styleFidelity.combined.power * 100).toFixed(1)}%</div>
                  <div style={{color: metrics.styleFidelity.combined.power >= params.targetPower ? '#28a745' : '#dc3545'}}>
                    {metrics.styleFidelity.combined.power >= params.targetPower ? '✓ Adequate' : '✗ Below target'}
                  </div>
                </div>
              </div>
            </div>
            
            <div style={{padding: '8px', background: '#f8f9fa', borderRadius: '2px', fontSize: '11px', color: '#495057'}}>
              <strong>Note:</strong> Power calculated for detecting {params.expectedAIvsRealDiff}pp difference (e.g., 60% vs 50% win rate).
              Confidence intervals assume within-person correlation = {params.withinPersonCorr.toFixed(2)}.
            </div>
          </div>

          {/* Cross-Model and Content-Style Metrics */}
          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px'}}>
            <div style={{
              background: '#ffffff',
              border: '1px solid #dee2e6',
              borderRadius: '3px',
              padding: '16px'
            }}>
              <h4 style={{margin: '0 0 12px 0', color: '#495057', fontSize: '14px', fontWeight: '500'}}>
                Cross-Model Comparisons (T2)
              </h4>
              <div style={{fontSize: '11px'}}>
                <div style={{marginBottom: '8px'}}>
                  <strong>Single Artist:</strong><br/>
                  ~{metrics.crossModel.singleArtist.nChoices} choices<br/>
                  CI: ±{metrics.crossModel.singleArtist.ci.toFixed(1)}pp
                </div>
                <div>
                  <strong>Pooled Across Artists:</strong><br/>
                  ~{metrics.crossModel.pooled.nChoices} choices<br/>
                  CI: ±{metrics.crossModel.pooled.ci.toFixed(1)}pp
                </div>
              </div>
            </div>
            
            <div style={{
              background: '#ffffff',
              border: '1px solid #dee2e6',
              borderRadius: '3px',
              padding: '16px'
            }}>
              <h4 style={{margin: '0 0 12px 0', color: '#495057', fontSize: '14px', fontWeight: '500'}}>
                Content-Style Disentanglement (T4)
              </h4>
              <div style={{fontSize: '11px'}}>
                <div style={{marginBottom: '4px'}}>
                  <strong>Detectable shifts (80% power):</strong>
                </div>
                <div>Lay: {metrics.contentStyle.lay.detectableShift}pp</div>
                <div>Expert: {metrics.contentStyle.expert.detectableShift}pp</div>
                <div>Combined: {metrics.contentStyle.combined.detectableShift}pp</div>
                <div style={{marginTop: '8px', color: metrics.contentStyle.combined.power >= 0.8 ? '#28a745' : '#dc3545'}}>
                  Power for {params.expectedContentStyleShift}pp: {(metrics.contentStyle.combined.power * 100).toFixed(1)}%
                </div>
              </div>
            </div>
          </div>

          {/* Power Visualization */}
          <div className="power-visualization">
            <h4>Power Analysis for Style Fidelity Index</h4>
            <div className="power-chart">
              <svg viewBox="0 0 400 220" className="power-svg">
                {/* Grid lines */}
                {[0, 0.2, 0.4, 0.6, 0.8, 1].map(val => (
                  <g key={val}>
                    <line 
                      x1="40" 
                      y1={180 - val * 140} 
                      x2="380" 
                      y2={180 - val * 140} 
                      stroke="#e0e0e0" 
                      strokeDasharray="2,2"
                    />
                    <text x="30" y={185 - val * 140} textAnchor="end" fontSize="10" fill="#666">
                      {(val * 100).toFixed(0)}%
                    </text>
                  </g>
                ))}
                
                {/* X-axis labels */}
                {[0, 100, 200, 300, 400, 500].map(val => (
                  <g key={val}>
                    <line 
                      x1={40 + (val / 500) * 340} 
                      y1={180} 
                      x2={40 + (val / 500) * 340} 
                      y2={185} 
                      stroke="#666"
                    />
                    <text x={40 + (val / 500) * 340} y={195} textAnchor="middle" fontSize="9" fill="#666">
                      {val}
                    </text>
                  </g>
                ))}
                
                {/* Power curve for 10pp difference */}
                <path
                  d={generatePowerCurve(10)}
                  fill="none"
                  stroke="#007bff"
                  strokeWidth="2"
                />
                
                {/* Power curve for expected difference */}
                {params.expectedAIvsRealDiff !== 10 && (
                  <path
                    d={generatePowerCurve(params.expectedAIvsRealDiff)}
                    fill="none"
                    stroke="#28a745"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                  />
                )}
                
                {/* Target power line */}
                <line
                  x1="40"
                  y1={180 - params.targetPower * 140}
                  x2="380"
                  y2={180 - params.targetPower * 140}
                  stroke="#dc3545"
                  strokeDasharray="5,5"
                />
                
                {/* Current sample size markers */}
                <circle
                  cx={40 + (params.nLaypeople / 500) * 340}
                  cy={180 - metrics.styleFidelity.lay.power * 140}
                  r="5"
                  fill="#007bff"
                />
                <text 
                  x={40 + (params.nLaypeople / 500) * 340} 
                  y={180 - metrics.styleFidelity.lay.power * 140 - 10} 
                  textAnchor="middle" 
                  fontSize="9" 
                  fill="#007bff"
                >
                  Lay
                </text>
                
                <circle
                  cx={40 + (params.nExperts / 500) * 340}
                  cy={180 - metrics.styleFidelity.expert.power * 140}
                  r="5"
                  fill="#ff9800"
                />
                <text 
                  x={40 + (params.nExperts / 500) * 340} 
                  y={180 - metrics.styleFidelity.expert.power * 140 + 20} 
                  textAnchor="middle" 
                  fontSize="9" 
                  fill="#ff9800"
                >
                  Expert
                </text>
                
                {/* Labels */}
                <text x="210" y="210" textAnchor="middle" fontSize="11" fill="#666">
                  Sample Size per Group
                </text>
                <text x="10" y="110" transform="rotate(-90 10 110)" textAnchor="middle" fontSize="11" fill="#666">
                  Statistical Power
                </text>
                
                {/* Legend */}
                <g transform="translate(250, 20)">
                  <rect x="0" y="0" width="130" height="45" fill="white" stroke="#ddd" strokeWidth="1" rx="2"/>
                  <line x1="5" y1="10" x2="25" y2="10" stroke="#007bff" strokeWidth="2"/>
                  <text x="30" y="14" fontSize="9" fill="#666">10pp difference</text>
                  {params.expectedAIvsRealDiff !== 10 && (
                    <>
                      <line x1="5" y1="22" x2="25" y2="22" stroke="#28a745" strokeWidth="2" strokeDasharray="5,5"/>
                      <text x="30" y="26" fontSize="9" fill="#666">{params.expectedAIvsRealDiff}pp difference</text>
                    </>
                  )}
                  <line x1="5" y1="34" x2="25" y2="34" stroke="#dc3545" strokeWidth="1" strokeDasharray="5,5"/>
                  <text x="30" y="38" fontSize="9" fill="#666">Target: {(params.targetPower * 100).toFixed(0)}%</text>
                </g>
              </svg>
            </div>
          </div>

          {/* Mixed-Effects Model Components */}
          <div style={{
            background: '#ffffff',
            border: '1px solid #dee2e6',
            borderRadius: '3px',
            padding: '16px',
            marginBottom: '16px'
          }}>
            <h4 style={{margin: '0 0 12px 0', color: '#495057', fontSize: '14px', fontWeight: '500'}}>
              Mixed-Effects Model Components
            </h4>
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px'}}>
              <div style={{fontSize: '11px'}}>
                <strong>Variance Components:</strong>
                <div>Participant (random intercept): {metrics.mixedEffects.varParticipant.toFixed(3)}</div>
                <div>Image (random intercept): {metrics.mixedEffects.varImage.toFixed(3)}</div>
                <div>Source × Participant (slope): {metrics.mixedEffects.varSlope.toFixed(3)}</div>
                <div>Residual: {metrics.mixedEffects.varResidual.toFixed(3)}</div>
              </div>
              <div style={{fontSize: '11px'}}>
                <strong>Design Efficiency:</strong>
                <div>ICC (participant): {metrics.mixedEffects.iccParticipant.toFixed(3)}</div>
                <div>Design effect: {metrics.mixedEffects.designEffect.toFixed(2)}</div>
                <div>Effective sample size: {Math.round(metrics.mixedEffects.effectiveSampleSize)}</div>
                <div>Avg cluster size: {metrics.mixedEffects.avgClusterSize} trials</div>
              </div>
            </div>
          </div>

          {/* Recommendations */}
          <div className="recommendations">
            <h4>Recommendations</h4>
            {recommendations.map((rec, idx) => (
              <div key={idx} className={`recommendation ${rec.type}`}>
                {rec.text}
              </div>
            ))}
          </div>

          {/* Preset Configurations */}
          <div className="preset-configs">
            <h4>Preset Configurations</h4>
            <div className="preset-buttons">
              <button onClick={() => applyPreset('minimal')}>
                Minimal Pilot
                <span>20 exp, 50 lay, 6 artists</span>
              </button>
              <button onClick={() => applyPreset('standard')}>
                Standard Study
                <span>50 exp, 200 lay, 12 artists</span>
              </button>
              <button onClick={() => applyPreset('comprehensive')}>
                Comprehensive
                <span>75 exp, 300 lay, 12 artists</span>
              </button>
              <button onClick={() => applyPreset('power-optimized')}>
                Power Optimized
                <span>60 exp, 240 lay, 90% power</span>
              </button>
            </div>
          </div>

          {/* Summary Metrics */}
          <div className="metrics-grid" style={{marginTop: '16px'}}>
            <div className="metric-card">
              <div className="metric-label">Total Participants</div>
              <div className="metric-value">{metrics.totalParticipants}</div>
              <div className="metric-detail">
                {params.nExperts} exp + {params.nLaypeople} lay
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-label">Duration</div>
              <div className="metric-value">{metrics.completionTimeFormatted}</div>
              <div className="metric-detail">
                per participant
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-label">Total Cost</div>
              <div className="metric-value">${metrics.estimatedCost.toLocaleString()}</div>
              <div className="metric-detail">
                ${params.costPerParticipant}/participant
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-label">Data Points</div>
              <div className="metric-value">{metrics.totalDataPoints.toLocaleString()}</div>
              <div className="metric-detail">
                ~{metrics.dataSizeMB.toFixed(1)} MB
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExperimentSimulator;